import type { ApiClient } from "../api";
import {
  deleteApiV1AuthSessions,
  deleteApiV1AuthSessionsBySession,
  deleteApiV1AuthTwoFactor,
  getApiV1AuthSessions,
  getApiV1AuthEmailVerifyByIdByHash,
  getApiV1AuthTwoFactorQrCode,
  getApiV1AuthTwoFactorRecoveryCodes,
  getApiV1AuthUser,
  getApiV1Profile,
  patchApiV1Profile,
  deleteApiV1ProfileAvatar,
  postApiV1AuthConfirmPassword,
  postApiV1AuthForgotPassword,
  postApiV1AuthLogin,
  postApiV1AuthLogout,
  postApiV1AuthEmailVerificationNotification,
  postApiV1AuthResetPassword,
  postApiV1AuthTwoFactorChallenge,
  postApiV1AuthTwoFactorConfirm,
  postApiV1AuthTwoFactorRecoveryCodes,
  postApiV1AuthTwoFactorSetup,
  postApiV1ProfileEmailChange,
  postApiV1ProfileEmailChangeConfirm,
  putApiV1ProfileAvatar,
  putApiV1AuthPassword,
} from "../api/generated";
import type {
  AuthenticatedResponse,
  IdentitySession,
  IdentityUser,
  LoginResponse,
  ProfileUpdate,
} from "./types";

export class IdentityApi {
  constructor(private readonly client: ApiClient) {}

  async login(
    email: string,
    password: string,
    remember = false,
  ): Promise<LoginResponse> {
    await this.client.bootstrapCsrf();
    return postApiV1AuthLogin(this.client, {
      body: { email, password, remember },
    });
  }

  challengeTwoFactor(input: {
    code?: string;
    recovery_code?: string;
  }): Promise<AuthenticatedResponse> {
    return postApiV1AuthTwoFactorChallenge(this.client, { body: input });
  }

  logout(): Promise<void> {
    return postApiV1AuthLogout(this.client);
  }

  async currentUser(): Promise<IdentityUser> {
    return (await getApiV1AuthUser(this.client)).data;
  }

  async profile(): Promise<IdentityUser> {
    return (await getApiV1Profile(this.client)).data;
  }

  async updateProfile(input: ProfileUpdate): Promise<IdentityUser> {
    return (await patchApiV1Profile(this.client, { body: input })).data;
  }

  async assignAvatar(fileId: string): Promise<IdentityUser> {
    return (await putApiV1ProfileAvatar(this.client, { body: { file_id: fileId } })).data;
  }

  async removeAvatar(): Promise<IdentityUser> {
    return (await deleteApiV1ProfileAvatar(this.client)).data;
  }

  async requestEmailChange(email: string): Promise<{ pending_email: string }> {
    return (await postApiV1ProfileEmailChange(this.client, { body: { email } }))
      .data;
  }

  async confirmEmailChange(token: string): Promise<IdentityUser> {
    return (
      await postApiV1ProfileEmailChangeConfirm(this.client, {
        body: { token },
      })
    ).data;
  }

  forgotPassword(email: string): Promise<{ message: string }> {
    return postApiV1AuthForgotPassword(this.client, { body: { email } });
  }

  resetPassword(input: {
    email: string;
    token: string;
    password: string;
    password_confirmation: string;
  }): Promise<{ message: string }> {
    return postApiV1AuthResetPassword(this.client, { body: input });
  }

  confirmPassword(password: string): Promise<void> {
    return postApiV1AuthConfirmPassword(this.client, { body: { password } });
  }

  changePassword(input: { current_password: string; password: string; password_confirmation: string }): Promise<void> {
    return putApiV1AuthPassword(this.client, { body: input });
  }

  sendEmailVerification(): Promise<void> {
    return postApiV1AuthEmailVerificationNotification(this.client);
  }

  verifyEmail(id: string, hash: string): Promise<void> {
    return getApiV1AuthEmailVerifyByIdByHash(this.client, {
      path: { id, hash },
    });
  }

  setupTwoFactor(): Promise<void> {
    return postApiV1AuthTwoFactorSetup(this.client);
  }

  twoFactorQrCode(): Promise<{ svg: string; url: string }> {
    return getApiV1AuthTwoFactorQrCode(this.client);
  }

  confirmTwoFactor(code: string): Promise<void> {
    return postApiV1AuthTwoFactorConfirm(this.client, { body: { code } });
  }

  async recoveryCodes(): Promise<string[]> {
    return (await getApiV1AuthTwoFactorRecoveryCodes(this.client)).data
      .recovery_codes;
  }

  async regenerateRecoveryCodes(): Promise<string[]> {
    return (await postApiV1AuthTwoFactorRecoveryCodes(this.client)).data
      .recovery_codes;
  }

  disableTwoFactor(): Promise<void> {
    return deleteApiV1AuthTwoFactor(this.client);
  }

  async sessions(): Promise<IdentitySession[]> {
    return (await getApiV1AuthSessions(this.client)).data;
  }

  destroySession(sessionId: string): Promise<void> {
    return deleteApiV1AuthSessionsBySession(this.client, {
      path: { session: sessionId },
    });
  }

  async destroyOtherSessions(): Promise<{ destroyed: number }> {
    return (await deleteApiV1AuthSessions(this.client)).data;
  }
}
